<template>
  <div class="ia-chat">
    <div class="ia-chat__header">
      <v-btn icon class="ia-chat__back" @click="volverAConversaciones">
        <v-icon color="#006093">mdi-arrow-left</v-icon>
      </v-btn>
      <div>
        <div class="ia-chat__title">Práctica con la IA</div>
        <div class="ia-chat__subtitle">Tema: {{ tema }}</div>
      </div>
    </div>

    <div v-if="errorAudio" class="ia-chat__error">
      {{ errorAudio }}
    </div>

    <div ref="mensajes" class="ia-chat__mensajes">
      <div
        v-for="(mensaje, index) in mensajes"
        :key="mensaje.id || index"
        class="ia-chat__bloque"
        :class="{ 'ia-chat__bloque--usuario': mensaje.rol === 'usuario' }"
      >
        <div class="ia-chat__fila" :class="{ 'ia-chat__fila--usuario': mensaje.rol === 'usuario' }">
          <div
            class="ia-chat__avatar"
            :class="mensaje.rol === 'ia' ? 'ia-chat__avatar--ia' : 'ia-chat__avatar--usuario'"
          >
            {{ mensaje.rol === 'ia' ? 'IA' : 'Tú' }}
          </div>

          <button
            v-if="mensaje.tipo === 'audio'"
            type="button"
            class="ia-chat__burbuja ia-chat__burbuja--audio"
            :class="{ 'ia-chat__burbuja--playing': reproduciendoId === mensaje.id }"
            @click="reproducirAudio(mensaje)"
          >
            <v-icon
              small
              color="#006093"
              class="mr-2"
            >
              {{ reproduciendoId === mensaje.id ? 'mdi-pause' : 'mdi-chart-bar' }}
            </v-icon>
            <span class="ia-chat__audio-duracion">{{ mensaje.duracion }}</span>
          </button>

          <div v-else class="ia-chat__burbuja">
            {{ mensaje.texto }}
          </div>

          <v-btn
            v-if="mensaje.rol === 'ia' && mensaje.tipo !== 'audio'"
            icon
            x-small
            class="ia-chat__tts"
            @click="reproducirTexto(mensaje.texto)"
          >
            <v-icon small color="#6b8296">mdi-volume-high</v-icon>
          </v-btn>
        </div>

        <div v-if="mensaje.feedback" class="ia-chat__feedback">
          <div
            v-if="mensaje.feedback.correccion"
            class="ia-chat__correccion"
          >
            {{ mensaje.feedback.correccion }}
          </div>
          <div
            v-for="(ok, i) in mensaje.feedback.oks"
            :key="i"
            class="ia-chat__ok"
          >
            <v-icon x-small color="#006093" class="mr-1">mdi-check</v-icon>
            {{ ok }}
          </div>
        </div>
      </div>
    </div>

    <!-- Barra normal: texto + mic -->
    <div v-if="!modoAudio" class="ia-chat__input-bar">
      <div class="ia-chat__composer">
        <div class="ia-chat__input-wrap">
          <input
            v-model="respuesta"
            type="text"
            class="ia-chat__input"
            placeholder="Escribe tu respuesta..."
            @keyup.enter="enviarTexto"
          >
        </div>
        <button
          type="button"
          class="ia-chat__mic"
          aria-label="Grabar audio"
          :disabled="iniciandoMic"
          @click="iniciarGrabacion"
        >
          <v-icon color="white">{{ iniciandoMic ? 'mdi-loading mdi-spin' : 'mdi-microphone' }}</v-icon>
        </button>
      </div>
    </div>

    <!-- Barra estilo WhatsApp: grabando / pausado -->
    <div v-else class="ia-chat__input-bar">
      <div class="ia-chat__rec-bar">
        <button type="button" class="ia-chat__rec-trash" aria-label="Eliminar audio" @click="cancelarGrabacion">
          <v-icon color="#1a2b3c">mdi-trash-can-outline</v-icon>
        </button>

        <div class="ia-chat__rec-body">
          <span class="ia-chat__rec-dot" :class="{ 'ia-chat__rec-dot--paused': pausado }"></span>
          <span class="ia-chat__rec-timer">{{ duracionFormateada }}</span>
          <div class="ia-chat__rec-wave" :class="{ 'ia-chat__rec-wave--paused': pausado }" aria-hidden="true">
            <i v-for="n in 28" :key="n"></i>
          </div>
          <button type="button" class="ia-chat__rec-pause" :aria-label="pausado ? 'Reanudar' : 'Pausar'" @click="togglePausa">
            <v-icon color="#e53935">{{ pausado ? 'mdi-microphone' : 'mdi-pause' }}</v-icon>
          </button>
        </div>

        <button type="button" class="ia-chat__rec-send" aria-label="Enviar audio" @click="enviarAudio">
          <v-icon color="white">mdi-send</v-icon>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgenteIAChat',

  data() {
    return {
      respuesta: '',
      modoAudio: false,
      pausado: false,
      segundos: 0,
      timerId: null,
      iniciandoMic: false,
      errorAudio: '',
      mediaRecorder: null,
      mediaStream: null,
      chunks: [],
      mimeType: 'audio/webm',
      audioPlayer: null,
      reproduciendoId: null,
      idSeq: 100,
      mensajes: [
        {
          id: 1,
          rol: 'ia',
          tipo: 'texto',
          texto: 'Hi! Did you read any good books recently?',
        },
        {
          id: 2,
          rol: 'usuario',
          tipo: 'audio',
          duracion: '0:07',
          url: null,
          feedback: {
            correccion: "Corrección: 'read' es irregular en pasado → read (se pronuncia /red/), no 'readed'.",
            oks: ['Pronunciación clara.'],
          },
        },
        {
          id: 3,
          rol: 'ia',
          tipo: 'texto',
          texto: 'Nice! What was it called?',
        },
        {
          id: 4,
          rol: 'usuario',
          tipo: 'texto',
          texto: "It was called 'It'.",
          feedback: {
            oks: ['Gramática correcta.'],
          },
        },
        {
          id: 5,
          rol: 'ia',
          tipo: 'texto',
          texto: 'What a strange name! What was it about?',
        },
        {
          id: 6,
          rol: 'usuario',
          tipo: 'audio',
          duracion: '0:04',
          url: null,
          feedback: {
            oks: ['Pronunciación clara.'],
          },
        },
      ],
      respuestasIa: [
        'Interesting! Tell me more about that.',
        'Great answer! Would you recommend it?',
        'Nice! What else do you enjoy reading?',
        'Awesome. How often do you read?',
      ],
    }
  },

  computed: {
    tema() {
      return this.$route.query.tema || 'Libros favoritos'
    },

    duracionFormateada() {
      return this.formatearSegundos(this.segundos)
    },
  },

  beforeDestroy() {
    this.detenerReproduccion()
    this.limpiarGrabacionCompleta()
    this.mensajes.forEach((m) => {
      if (m.url) URL.revokeObjectURL(m.url)
    })
  },

  mounted() {
    this.$nextTick(this.scrollAlFinal)
  },

  methods: {
    nextId() {
      this.idSeq += 1
      return this.idSeq
    },

    volverAConversaciones() {
      this.$router.replace({
        name: 'AgenteIA',
        query: { skipLoader: '1' },
      })
    },

    formatearSegundos(total) {
      const m = Math.floor(total / 60)
      const s = total % 60
      return `${m}:${String(s).padStart(2, '0')}`
    },

    scrollAlFinal() {
      const el = this.$refs.mensajes
      if (el) el.scrollTop = el.scrollHeight
    },

    elegirMimeType() {
      const candidatos = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
      ]
      if (!window.MediaRecorder || !MediaRecorder.isTypeSupported) {
        return 'audio/webm'
      }
      return candidatos.find((t) => MediaRecorder.isTypeSupported(t)) || ''
    },

    reproducirTexto(texto) {
      if (!window.speechSynthesis) return
      const utter = new SpeechSynthesisUtterance(texto)
      utter.lang = 'en-US'
      window.speechSynthesis.cancel()
      window.speechSynthesis.speak(utter)
    },

    detenerReproduccion() {
      if (this.audioPlayer) {
        this.audioPlayer.pause()
        this.audioPlayer = null
      }
      this.reproduciendoId = null
    },

    reproducirAudio(mensaje) {
      if (!mensaje.url) return

      if (this.reproduciendoId === mensaje.id) {
        this.detenerReproduccion()
        return
      }

      this.detenerReproduccion()
      const audio = new Audio(mensaje.url)
      this.audioPlayer = audio
      this.reproduciendoId = mensaje.id
      audio.onended = () => {
        this.reproduciendoId = null
        this.audioPlayer = null
      }
      audio.onerror = () => {
        this.reproduciendoId = null
        this.audioPlayer = null
      }
      audio.play().catch(() => {
        this.reproduciendoId = null
        this.audioPlayer = null
      })
    },

    enviarTexto() {
      const texto = this.respuesta.trim()
      if (!texto) return

      this.mensajes.push({
        id: this.nextId(),
        rol: 'usuario',
        tipo: 'texto',
        texto,
        feedback: {
          oks: ['Gramática correcta.'],
        },
      })
      this.respuesta = ''
      this.$nextTick(this.scrollAlFinal)
      this.responderIa()
    },

    async iniciarGrabacion() {
      this.errorAudio = ''
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.errorAudio = 'Tu navegador no soporta grabación de audio.'
        return
      }
      if (!window.MediaRecorder) {
        this.errorAudio = 'MediaRecorder no está disponible en este entorno.'
        return
      }

      this.iniciandoMic = true
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        this.mediaStream = stream
        this.chunks = []
        this.mimeType = this.elegirMimeType()

        const options = this.mimeType ? { mimeType: this.mimeType } : undefined
        this.mediaRecorder = new MediaRecorder(stream, options)
        this.mimeType = this.mediaRecorder.mimeType || this.mimeType || 'audio/webm'

        this.mediaRecorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) this.chunks.push(e.data)
        }

        this.mediaRecorder.start(200)
        this.modoAudio = true
        this.pausado = false
        this.segundos = 0
        this.iniciarTimer()
      } catch (err) {
        this.errorAudio = 'No se pudo acceder al micrófono. Revisa los permisos del navegador o de la app.'
        this.limpiarStream()
      } finally {
        this.iniciandoMic = false
      }
    },

    iniciarTimer() {
      this.limpiarTimer()
      this.timerId = setInterval(() => {
        if (!this.pausado) this.segundos += 1
      }, 1000)
    },

    limpiarTimer() {
      if (this.timerId) {
        clearInterval(this.timerId)
        this.timerId = null
      }
    },

    limpiarStream() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((t) => t.stop())
        this.mediaStream = null
      }
      this.mediaRecorder = null
      this.chunks = []
    },

    limpiarGrabacionCompleta() {
      this.limpiarTimer()
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        try {
          this.mediaRecorder.stop()
        } catch (e) { /* ignore */ }
      }
      this.limpiarStream()
      this.modoAudio = false
      this.pausado = false
      this.segundos = 0
    },

    togglePausa() {
      if (!this.mediaRecorder) return

      if (this.pausado) {
        if (this.mediaRecorder.state === 'paused') {
          this.mediaRecorder.resume()
        }
        this.pausado = false
      } else {
        if (this.mediaRecorder.state === 'recording' && typeof this.mediaRecorder.pause === 'function') {
          try {
            this.mediaRecorder.pause()
            this.pausado = true
          } catch (e) {
            // Algunos WebViews no soportan pause; solo pausamos el timer visual
            this.pausado = true
          }
        } else {
          this.pausado = true
        }
      }
    },

    cancelarGrabacion() {
      this.limpiarGrabacionCompleta()
    },

    enviarAudio() {
      if (!this.mediaRecorder) {
        this.cancelarGrabacion()
        return
      }

      const duracion = this.duracionFormateada
      const segundos = this.segundos
      this.limpiarTimer()

      const finalizar = () => {
        const blob = new Blob(this.chunks, { type: this.mimeType || 'audio/webm' })
        this.limpiarStream()
        this.modoAudio = false
        this.pausado = false
        this.segundos = 0

        if (!blob.size && segundos < 1) {
          this.errorAudio = 'La grabación es demasiado corta.'
          return
        }

        const url = URL.createObjectURL(blob)
        // Local en memoria de la sesión: se pierde al refrescar. Sin BD.
        this.mensajes.push({
          id: this.nextId(),
          rol: 'usuario',
          tipo: 'audio',
          duracion,
          url,
          blob,
          mimeType: this.mimeType,
          feedback: {
            oks: ['Pronunciación clara.'],
          },
        })
        this.$nextTick(this.scrollAlFinal)
        this.responderIa('Thanks for sharing! Can you explain a bit more?')
      }

      if (this.mediaRecorder.state === 'inactive') {
        finalizar()
        return
      }

      this.mediaRecorder.onstop = finalizar
      try {
        if (this.mediaRecorder.state === 'paused') {
          this.mediaRecorder.resume()
        }
        this.mediaRecorder.stop()
      } catch (e) {
        finalizar()
      }
    },

    responderIa(textoFijo) {
      setTimeout(() => {
        const texto = textoFijo || this.respuestasIa[Math.floor(Math.random() * this.respuestasIa.length)]
        this.mensajes.push({
          id: this.nextId(),
          rol: 'ia',
          tipo: 'texto',
          texto,
        })
        this.$nextTick(this.scrollAlFinal)
      }, 700)
    },
  },
}
</script>

<style scoped>
.ia-chat {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: calc(56px + env(safe-area-inset-bottom, 0px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f4f7fa;
  color: #1a2b3c;
  z-index: 2;
}

.ia-chat__header {
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px 12px;
  flex-shrink: 0;
  background: #f4f7fa;
  border-bottom: 1px solid #e4edf3;
  box-sizing: border-box;
}

.ia-chat__back {
  margin-left: -8px;
  margin-top: -4px;
}

.ia-chat__title {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: #006093;
}

.ia-chat__subtitle {
  margin-top: 2px;
  font-size: 13px;
  color: #6b8296;
}

.ia-chat__error {
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 8px 16px;
  font-size: 13px;
  color: #b71c1c;
  background: #ffebee;
  flex-shrink: 0;
  box-sizing: border-box;
}

.ia-chat__mensajes {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 12px 16px 16px;
  box-sizing: border-box;
}

.ia-chat__bloque {
  margin-bottom: 16px;
}

.ia-chat__bloque--usuario {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.ia-chat__fila {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.ia-chat__fila--usuario {
  flex-direction: row-reverse;
}

.ia-chat__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 4px;
}

.ia-chat__avatar--ia {
  background: #006093;
  color: #fff;
}

.ia-chat__avatar--usuario {
  background: #d5e0e8;
  color: #004f72;
}

.ia-chat__burbuja {
  max-width: 72%;
  background: #fff;
  border: 1px solid #dce6ee;
  border-radius: 14px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 1.4;
  color: #1a2b3c;
  box-shadow: 0 1px 4px rgba(0, 96, 147, 0.05);
}

.ia-chat__burbuja--audio {
  display: inline-flex;
  align-items: center;
  min-width: 110px;
  cursor: pointer;
  font: inherit;
  text-align: left;
  color: #006093;
}

.ia-chat__burbuja--audio:hover {
  border-color: #9fc4d9;
}

.ia-chat__burbuja--playing {
  border-color: #006093;
  background: #e8f3f9;
}

.ia-chat__audio-duracion {
  font-size: 14px;
  font-weight: 600;
  color: #006093;
  font-variant-numeric: tabular-nums;
}

.ia-chat__tts {
  margin-top: 6px;
}

.ia-chat__feedback {
  margin-top: 8px;
  margin-right: 36px;
  max-width: 85%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.ia-chat__correccion {
  width: 100%;
  border: 1px solid #9fc4d9;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.4;
  color: #004f72;
  background: #e8f3f9;
}

.ia-chat__ok {
  display: inline-flex;
  align-items: center;
  border: 1px solid #c5d9e6;
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 13px;
  color: #006093;
  background: #fff;
}

.ia-chat__input-bar {
  flex-shrink: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px 12px;
  background: #f4f7fa;
  border-top: 1px solid #e4edf3;
  box-sizing: border-box;
}

.ia-chat__composer {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 640px;
}

.ia-chat__input-wrap {
  flex: 1;
  background: #fff;
  border: 1px solid #dce6ee;
  border-radius: 999px;
  padding: 0 16px;
  box-shadow: 0 2px 8px rgba(0, 96, 147, 0.06);
}

.ia-chat__input {
  width: 100%;
  height: 44px;
  border: none;
  outline: none;
  background: transparent;
  color: #1a2b3c;
  font-size: 14px;
}

.ia-chat__input::placeholder {
  color: #8fa3b3;
}

.ia-chat__mic {
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: #006093;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 96, 147, 0.25);
}

.ia-chat__mic:disabled {
  opacity: 0.7;
  cursor: wait;
}

.ia-chat__rec-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 640px;
}

.ia-chat__rec-trash {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid #006093;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.ia-chat__rec-body {
  flex: 1;
  min-width: 0;
  height: 48px;
  background: #fff;
  border: 1px solid #dce6ee;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px 0 14px;
  box-shadow: 0 2px 8px rgba(0, 96, 147, 0.06);
}

.ia-chat__rec-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e53935;
  flex-shrink: 0;
  animation: ia-rec-blink 1s ease-in-out infinite;
}

.ia-chat__rec-dot--paused {
  animation: none;
  opacity: 0.45;
}

.ia-chat__rec-timer {
  font-size: 14px;
  font-weight: 600;
  color: #1a2b3c;
  min-width: 36px;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}

.ia-chat__rec-wave {
  flex: 1;
  min-width: 0;
  height: 22px;
  display: flex;
  align-items: center;
  gap: 2px;
  overflow: hidden;
}

.ia-chat__rec-wave i {
  flex: 1;
  max-width: 3px;
  height: 6px;
  border-radius: 2px;
  background: #9bb7c9;
  animation: ia-wave 0.9s ease-in-out infinite;
}

.ia-chat__rec-wave i:nth-child(3n) { animation-delay: 0.1s; height: 14px; }
.ia-chat__rec-wave i:nth-child(3n+1) { animation-delay: 0.25s; height: 8px; }
.ia-chat__rec-wave i:nth-child(3n+2) { animation-delay: 0.4s; height: 18px; }
.ia-chat__rec-wave i:nth-child(5n) { height: 11px; }

.ia-chat__rec-wave--paused i {
  animation: none;
  opacity: 0.5;
}

.ia-chat__rec-pause {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
}

.ia-chat__rec-send {
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: #006093;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 96, 147, 0.25);
}

@keyframes ia-rec-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
}

@keyframes ia-wave {
  0%, 100% { transform: scaleY(0.55); }
  50% { transform: scaleY(1); }
}

@media (min-width: 960px) {
  .ia-chat__header,
  .ia-chat__mensajes,
  .ia-chat__error {
    max-width: 640px;
  }

  .ia-chat__composer,
  .ia-chat__rec-bar {
    max-width: 560px;
  }

  .ia-chat__burbuja {
    max-width: 60%;
  }
}
</style>
